import { Spinner, theme } from '@zappyrent/hundop';
import { upperFirst } from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DocumentListItem,
  DocumentListFilter,
} from '../../components/documents';
import { MainLayout } from '../../components/MainLayout';
import bookingDocumentsAPI from '../../services/api/booking-documents';
import { useUser } from '../../shared/hooks';
import useStore from '../../store';
import {
  BookingWithDocuments,
  BookingWithDocumentsStatus,
} from '../../types/documents';
import {
  StyledEmptyList,
  StyledList,
  StyledListWrapper,
  StyledTitle,
  StyledTitleWrapper,
} from './Documents.styles';

export function Documents(): JSX.Element | null {
  const { t } = useTranslation(['booking']);
  const isLoadingUser = useUser();
  const { user } = useStore(state => ({
    user: state.user,
  }));
  const [bookings, setBookings] = useState<Array<BookingWithDocuments> | []>(
    [],
  );
  const [bookingStatusFilter, setBookingStatusFilter] = useState([
    BookingWithDocumentsStatus.Active,
  ]);
  const [isLoadingBookings, setIsLoadingBookings] = useState(true);

  useEffect(() => {
    if (!user?.type) return;

    setIsLoadingBookings(true);
    bookingDocumentsAPI
      .getBookingsWithDocuments({
        userType: user.type,
        status: bookingStatusFilter,
      })
      .then(data => {
        setBookings(data);
      })
      .finally(() => setIsLoadingBookings(false));
  }, [user, bookingStatusFilter]);

  function handleBookingStatusFilterChange(
    newBookingStatusFilter: Array<BookingWithDocumentsStatus>,
  ) {
    setBookingStatusFilter(newBookingStatusFilter);
  }

  const bookingsWithDocuments = bookings.filter(
    booking => booking.documents.length > 0,
  );

  if (isLoadingUser) return null;

  return (
    <MainLayout>
      <StyledTitleWrapper>
        <StyledTitle>{upperFirst(t('booking:documents'))}</StyledTitle>
        {isLoadingBookings && (
          <Spinner
            foregroundColor={theme.colors.mainAlternate}
            size={20}
            strokeSize={2}
          />
        )}
      </StyledTitleWrapper>
      <DocumentListFilter
        activeOptions={bookingStatusFilter}
        onActiveOptionsChange={handleBookingStatusFilterChange}
      />
      {!isLoadingBookings && (
        <StyledListWrapper aria-live="polite">
          {bookingsWithDocuments.length > 0 ? (
            <StyledList>
              {bookingsWithDocuments.map(booking => (
                <DocumentListItem key={booking.id} {...booking} />
              ))}
            </StyledList>
          ) : (
            <StyledEmptyList>
              {upperFirst(t('booking:noResultsFound'))}
            </StyledEmptyList>
          )}
        </StyledListWrapper>
      )}
    </MainLayout>
  );
}
